// frontend/src/AuthGate.jsx
/**
 * AuthGate — top-level authentication boundary.
 *
 * Renders children ONLY when the user has a valid Supabase session.
 * Otherwise:
 *   - During initial session check: full-screen spinner (no chrome)
 *   - Unauthenticated: AuthBackdrop (faux UI, blurred, no live data) +
 *     Login dialog centered on top — the "login overlays the app" UX.
 *
 * Mounted between <Router> and the rest of the app shell, so that
 * NotificationsProvider, SidebarAndHeaderBar, Bell, etc. NEVER mount
 * for unauthenticated visitors. No data is fetched, no API calls fire.
 * AuthBackdrop is intentionally static (Skeleton + Box only) — it
 * conveys "an app is here" without violating the security context.
 *
 * Public paths (e.g., /vendor-reports/public/:id) bypass the gate so
 * customer-facing report URLs continue to work without login.
 *
 * Security context (2026-04-30): prior to this gate, SidebarAndHeaderBar
 * + Bell + NotificationsProvider rendered unconditionally. usePresence
 * + useNotifications fired unauthenticated API calls. The whole chrome
 * was visible without login.
 */
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Box } from '@mui/material'
import { useSession } from '@supabase/auth-helpers-react'
import Login from './Login'
import AuthBackdrop from './auth/AuthBackdrop'

const PUBLIC_PATH_PREFIXES = [
  '/vendor-reports/public/',
]

function isPublicPath(pathname) {
  return PUBLIC_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix))
}

export default function AuthGate({ children }) {
  const session = useSession()
  const location = useLocation()
  const [waited, setWaited] = useState(false)

  useEffect(() => {
    // Give Supabase 500ms to rehydrate the session from local storage
    // before deciding "not authenticated". Same heuristic ProtectedRoute uses.
    const t = setTimeout(() => setWaited(true), 500)
    return () => clearTimeout(t)
  }, [])

  // Public paths bypass the gate completely
  if (isPublicPath(location.pathname)) {
    return children
  }

  // Initial session-check loading state — render the AuthBackdrop
  // (Skeleton-only faux UI). NO spinner per the project UX rule
  // (skeletons reflect content shape, never block with a generic ring).
  // AuthBackdrop has no live data + no API calls so it's safe pre-auth.
  if (!waited || session === undefined) {
    return (
      <Box sx={{ position: 'fixed', inset: 0 }}>
        <AuthBackdrop />
      </Box>
    )
  }

  // Unauthenticated — backdrop (blurred faux UI) + Login dialog overlay.
  if (!session) {
    return (
      <Box sx={{ position: 'fixed', inset: 0 }}>
        <AuthBackdrop />
        {/* Soft scrim above the blurred backdrop, below the login card.
            Adds contrast without fully hiding the app shell behind. */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: theme => theme.palette.mode === 'dark'
              ? 'rgba(15, 23, 42, 0.55)'
              : 'rgba(15, 23, 42, 0.18)',
            backdropFilter: 'blur(2px)',
          }}
        />
        {/* Login modal, centered, in sharp focus */}
        <Box
          role="dialog"
          aria-modal="true"
          aria-label="Anmelden"
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'center',
            py: { xs: 6, sm: 4 },
            px: 2,
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 420,
              // Login renders its own Paper card; this Box is just the
              // sizing constraint for the centered layout.
            }}
          >
            <Login />
          </Box>
        </Box>
      </Box>
    )
  }

  // Authenticated — let the rest of the app render
  return children
}
